import React, { useState, useEffect } from 'react';
import {
  ArrowLeft,
  CheckCircle,
  XCircle,
  Clock,
  User,
  Briefcase,
  DollarSign,
  Phone,
  FileText,
  Download,
  Calendar,
  MessageSquare,
  AlertCircle,
  Loader2,
  Award,
  MapPin
} from 'lucide-react';
import { serviceProviderApplicationsService } from '../../services/api';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

interface Document{
  size: number;
  mimetype: string;
  path: string;
  filename: string;
  originalname: string;
}

interface ServiceProviderApplication {
  id: number;
  user_id: number;
  business_name: string;
  business_description: string;
  service_categories: string[];
  experience_years: number;
  hourly_rate: number;
  availability_hours: string;
  email: string;
  location: string;
  application_status: 'pending' | 'approved' | 'rejected';
  admin_comments?: string;
  submitted_at: string;
  reviewed_at?: string;
  last_name: string;
  first_name: string;
  phone_number: string;
  documents: Document[];
}

interface AdminServiceProviderApplicationDetailProps {
  applicationId: number;
  onBack: () => void;
  onApplicationUpdated: () => void;
}

const AdminServiceProviderApplicationDetail: React.FC<AdminServiceProviderApplicationDetailProps> = ({
  applicationId,
  onBack,
  onApplicationUpdated
}) => {
  const [application, setApplication] = useState<ServiceProviderApplication | null>(null);
  const [loading, setLoading] = useState(true);
  const [reviewing, setReviewing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reviewAction, setReviewAction] = useState<'approve' | 'reject' | null>(null);
  const [adminComments, setAdminComments] = useState('');
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [downloadingFile, setDownloadingFile] = useState<string | null>(null);

  useEffect(() => {
    fetchApplication();
  }, [applicationId]);

  const fetchApplication = async () => {
    try {
      setLoading(true);
      const response = await serviceProviderApplicationsService.getApplicationById(applicationId);
      setApplication(response.data);
      if (response.data.admin_comments) {
        setAdminComments(response.data.admin_comments);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch application');
    } finally {
      setLoading(false);
    }
  };

  const handleReviewSubmit = async () => {
    if (!reviewAction || !application) return;

    try {
      setReviewing(true);
      
      if (reviewAction === 'approve') {
        await serviceProviderApplicationsService.approveApplication(application.id, adminComments);
      } else {
        await serviceProviderApplicationsService.rejectApplication(application.id, adminComments);
      }

      await fetchApplication();
      onApplicationUpdated();
      setShowReviewModal(false);
      setReviewAction(null);
      setAdminComments('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to review application');
    } finally {
      setReviewing(false);
    }
  };

  const handleReviewAction = (action: 'approve' | 'reject') => {
    setReviewAction(action);
    setShowReviewModal(true);
  };

  const handleDownloadFile = async (fileDocument: Document) => {
    try {
      setDownloadingFile(fileDocument.filename);
      
      // Create authenticated axios instance
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/api/files/download-path/${fileDocument.path || `service-provider-documents/${fileDocument.filename}`}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          },
          responseType: 'blob' // Important for file downloads
        }
      );
      
      // Create blob URL and trigger download
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileDocument.originalname || fileDocument.filename;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Download error:', error);
      setError('Failed to download file. Please try again.');
    } finally {
      setDownloadingFile(null);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: Clock },
      approved: { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle },
      rejected: { bg: 'bg-red-100', text: 'text-red-800', icon: XCircle },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || {
      bg: 'bg-gray-100',
      text: 'text-gray-800',
      icon: AlertCircle
    };
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        <Icon className="w-4 h-4 mr-1" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <p className="mt-2 text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!application) {
    return (
      <div className="p-6">
        <p className="text-gray-500">Application not found</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={onBack}
            className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Applications
          </button>
          {getStatusBadge(application.application_status)}
        </div>
        
        <h1 className="text-2xl font-bold text-gray-900">Service Provider Application #{application.id}</h1>
        <p className="text-gray-600 mt-1">Submitted on {formatDate(application.submitted_at)}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Information */}
        <div className="lg:col-span-2 space-y-6">
          {/* Applicant Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <User className="h-5 w-5 mr-2 text-blue-600" />
              Applicant Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Full Name</label>
                <p className="mt-1 text-sm text-gray-900">{application.first_name} {application.last_name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <p className="mt-1 text-sm text-gray-900">{application.email}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Phone</label>
                <p className="mt-1 text-sm text-gray-900">{application.phone_number}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Location</label>
                <p className="mt-1 text-sm text-gray-900">{application.location}</p>
              </div>
            </div>
          </div>

          {/* Business Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Briefcase className="h-5 w-5 mr-2 text-blue-600" />
              Business Information
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Business Name</label>
                <p className="mt-1 text-sm text-gray-900">{application.business_name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Business Description</label>
                <p className="mt-1 text-sm text-gray-900">{application.business_description}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Service Categories</label>
                <div className="mt-1 flex flex-wrap gap-2">
                  {application.service_categories.map((category, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                    >
                      {category}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Service Details */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Award className="h-5 w-5 mr-2 text-blue-600" />
              Service Details
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Experience</label>
                <p className="mt-1 text-sm text-gray-900">{application.experience_years} years</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Hourly Rate</label>
                <p className="mt-1 text-sm text-gray-900">${application.hourly_rate}/hour</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Availability</label>
                <p className="mt-1 text-sm text-gray-900">{application.availability_hours}</p>
              </div>
            </div>
          </div>

          {/* Documents */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <FileText className="h-5 w-5 mr-2 text-blue-600" />
              Documents
            </h2>
            <div className="space-y-3">
              {application.documents && application.documents.length > 0 ? (
                application.documents.map((document, index) => (
                  <div key={index} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                    <div className="flex items-center">
                      <FileText className="h-5 w-5 text-gray-400 mr-2" />
                      <div className="flex flex-col">
                        <span className="text-sm text-gray-900">{document.originalname}</span>
                        <span className="text-xs text-gray-500">
                          {document.mimetype} • {(document.size / 1024).toFixed(1)} KB
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => handleDownloadFile(document)}
                      disabled={downloadingFile === document.filename}
                      className="flex items-center text-blue-600 hover:text-blue-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {downloadingFile === document.filename ? (
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      ) : (
                        <Download className="h-4 w-4 mr-1" />
                      )}
                      {downloadingFile === document.filename ? 'Downloading...' : 'Download'}
                    </button>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500 text-center py-4">No documents uploaded</p>
              )}
            </div>
          </div>

          {/* Admin Comments */}
          {application.admin_comments && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <MessageSquare className="h-5 w-5 mr-2 text-blue-600" />
                Admin Comments
              </h2>
              <p className="text-sm text-gray-700">{application.admin_comments}</p>
              {application.reviewed_at && (
                <p className="text-xs text-gray-500 mt-2">
                  Reviewed on {formatDate(application.reviewed_at)}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Actions Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow p-6 sticky top-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Actions</h3>
            
            {application.application_status === 'pending' && (
              <div className="space-y-3">
                <button
                  onClick={() => handleReviewAction('approve')}
                  className="w-full flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Approve Application
                </button>
                
                <button
                  onClick={() => handleReviewAction('reject')}
                  className="w-full flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Reject Application
                </button>
              </div>
            )}

            {application.application_status !== 'pending' && (
              <div className="text-center py-4">
                <p className="text-sm text-gray-600">
                  This application has been {application.application_status}
                </p>
                {application.reviewed_at && (
                  <p className="text-xs text-gray-500 mt-1">
                    on {formatDate(application.reviewed_at)}
                  </p>
                )}
              </div>
            )}

            {/* Application Details */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h4 className="text-sm font-medium text-gray-900 mb-3">Application Details</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Application ID:</span>
                  <span className="text-gray-900">{application.id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Applicant ID:</span>
                  <span className="text-gray-900">{application.user_id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Status:</span>
                  {getStatusBadge(application.application_status)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Review Modal */}
      {showReviewModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                {reviewAction === 'approve' ? 'Approve' : 'Reject'} Application
              </h3>
            </div>
            
            <div className="px-6 py-4">
              <p className="text-sm text-gray-600 mb-4">
                Are you sure you want to {reviewAction} this service provider application?
                {reviewAction === 'reject' && ' Please provide a reason for rejection.'}
              </p>
              
              <div className="mb-4">
                <label htmlFor="adminComments" className="block text-sm font-medium text-gray-700 mb-2">
                  {reviewAction === 'approve' ? 'Comments (optional)' : 'Reason for rejection *'}
                </label>
                <textarea
                  id="adminComments"
                  rows={4}
                  value={adminComments}
                  onChange={(e) => setAdminComments(e.target.value)}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder={reviewAction === 'approve' 
                    ? 'Add any additional comments...' 
                    : 'Please provide the reason for rejection...'}
                  required={reviewAction === 'reject'}
                />
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowReviewModal(false);
                  setReviewAction(null);
                  setAdminComments('');
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                disabled={reviewing}
              >
                Cancel
              </button>
              <button
                onClick={handleReviewSubmit}
                disabled={reviewing || (reviewAction === 'reject' && !adminComments.trim())}
                className={`px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                  reviewAction === 'approve'
                    ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
                    : 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {reviewing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  reviewAction === 'approve' ? 'Approve' : 'Reject'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminServiceProviderApplicationDetail;